import React from "react";
import {
    DashboardActions,
    DashboardStore, getClientList,
    getPerformanceData,
    list,
    remove,
    showData,
} from "./DashboardStore";
import {connect} from "react-redux";
import Datatable from "../../components/datatable";
import {Modal, ModalBody, ModalHeader} from "reactstrap";
import _ from "lodash";
import {Link} from "react-router-dom";
import PerformanceDashboard from "../dashboardV2/PerformanceDashboard";
import {Calendar, FileText, Users} from "lucide-react";

const mapStateToProps = (state) => ({
    ...state.dashboard,
});

let updateTime = Date.now();
const updateInterval = 3000;
const INACTIVITY_LIMIT = 30000;

class Dashboard extends React.Component {
    state = {
        userActive: true,
        expanded: false,
        historyExpanded: false,
        historyOverallPerformance: 0,
        liveOverallPerformance: 0,
    };

    inactivityTimer = null;

    handleUserActivity = () => {
        clearTimeout(this.inactivityTimer);

        if (!this.state.userActive) {
            this.setState({userActive: true});
        }

        this.inactivityTimer = setTimeout(() => {
            this.setState({userActive: false});
        }, INACTIVITY_LIMIT);
    };

    componentDidMount() {
        window.addEventListener("mousemove", this.handleUserActivity);
        document.title = `${process.env.REACT_APP_PLATFORM?.toUpperCase()} - NEXT GEN`;
        DashboardStore.dispatch(getClientList())

        if (!this.props.gettingData) {
            this.props.setStageOption();
        }
    }

    componentWillUnmount() {
        window.removeEventListener("mousemove", this.handleUserActivity);
        clearTimeout(this.inactivityTimer);
    }

    componentDidUpdate(prevProps) {

        this.autoRefresh(prevProps);

        if (this.shouldReloadPage(prevProps) && this.state.userActive) {
            DashboardStore.dispatch(list(prevProps));
        }
    }

    autoRefresh = (prevProps) => {
        setTimeout(() => {
            const shouldRefresh =
                !this.props.gettingData &&
                this.state.userActive &&
                !this.props.isHistoryView &&
                Date.now() - updateTime > updateInterval;

            if (shouldRefresh) {
                DashboardStore.dispatch(list(prevProps));
                updateTime = Date.now();
            }
        }, updateInterval);
    };

    shouldReloadPage = (prevProps) =>
        ["pageLimit", "sortColumn", "currentPage"].some(
            (field) => this.props[field] !== prevProps[field]
        ) ||
        !_.isEqual(this.props.filterData, prevProps.filterData);

    deleteAllCurrentPage = () => {
        const rows = this.props.data;

        if (!rows?.length) {
            alert("No rows to delete on this page.");
            return;
        }

        if (!confirm(`Delete all ${rows.length} rows
                      from this page ?`)) return;

        rows.forEach((row) => DashboardStore.dispatch(remove(row)));
    };

    actionsOptions = ({button: Btn, row}) => (
        <div>
            <Btn
                onClick={() =>
                    confirm("Remove this key?") &&
                    DashboardStore.dispatch(remove(row))
                }
            >
                <i className="action fa fa-trash-o"/> Remove
            </Btn>

            <Btn onClick={() => DashboardStore.dispatch(showData(row))}>
                <i className="action fa fa-eye"/> Show Data
            </Btn>
        </div>
    );

    renderDataModal() {
        if (_.isEmpty(this.props.selectedRowData)) return null;

        const row = this.props.selectedRowData;

        return (
            <Modal fullscreen isOpen toggle={this.props.closeDataModal}>
                <ModalHeader>
                    Feed Id - {row.feed_id} | Client Id - {row.client_id} | Client -{" "}
                    {row.client}
                </ModalHeader>

                <ModalBody>
                    <table border={1} className="table">
                        {this.renderKeyValueRows(row)}
                    </table>

                    <textarea
                        rows="25"
                        style={{width: "100%", height: "100%"}}
                        defaultValue={JSON.stringify(row, null, 4)}
                    />
                </ModalBody>
            </Modal>
        );
    }

    renderKeyValueRows(data) {
        const columns = [
            "filename",
            "type",
            "current_stage",
            "status",
            "batchId",
            "start_time",
            "end_time",
        ];

        return [
            <tr key="header">
                {columns.map((col) => (
                    <td key={col} style={styles.cell}>
                        {col.replaceAll("_", " ").toUpperCase()}
                    </td>
                ))}
            </tr>,
            <tr key="values">
                {columns.map((col) => (
                    <td key={col} style={styles.cell}>
                        {data[col]}
                    </td>
                ))}
            </tr>,
        ];
    }

    render() {
        return (
            <section>
                <div className="container-fluid">


                    {!this.state.userActive && (
                        <div className="inactive-banner">⏸ Paused due to inactivity from
                            last {(INACTIVITY_LIMIT / 1000)} seconds </div>
                    )}


                    {/* Report Header & Filters */}
                    <div
                        className="bg-slate-900 p-6 rounded-xl shadow-lg text-white flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
                        <div>
                            <h1 className="text-2xl font-bold flex items-center gap-3">
                                <FileText className="text-emerald-400"/>
                                Live Processing Document List
                            </h1>
                            <p className="text-slate-400 text-sm mt-1">You can do filter and track processing</p>
                        </div>

                        {/* Filters */}
                        <div
                            className="flex flex-wrap items-center gap-4 bg-slate-800/50 p-2 rounded-lg border border-slate-700">

                            <div className="flex flex-col gap-1">
                                <label
                                    className="text-[10px] uppercase font-bold text-slate-400 tracking-wider ml-1">Flow</label>
                                <div className="relative">
                                    <Calendar className="absolute left-2.5 top-2 text-slate-400" size={14}/>
                                    <select
                                        value={this.props.selectedFlow}
                                        onChange={(e) => this.props.setSelectedFlow(e.target.value)}
                                        className="pl-8 pr-8 py-1.5 bg-slate-800 border border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none appearance-none cursor-pointer hover:bg-slate-700 transition-colors"
                                    >

                                        <option value="">ALL</option>
                                        <option>WEB</option>
                                        <option>FEED</option>
                                        <option>FSCC</option>
                                        <option>LARGE</option>
                                    </select>
                                </div>
                            </div>

                            <div className="flex flex-col gap-1">
                                <label
                                    className="text-[10px] uppercase font-bold text-slate-400 tracking-wider ml-1">Client</label>
                                <div className="relative">
                                    <Users className="absolute left-2.5 top-2 text-slate-400" size={14}/>
                                    <select
                                        value={this.props.selectedClient}
                                        onChange={(e) => this.props.setSelectedClient(e.target.value)}
                                        className="pl-8 pr-8 py-1.5 bg-slate-800 border border-slate-600 rounded-md text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none appearance-none cursor-pointer hover:bg-slate-700 transition-colors min-w-[140px]"
                                    >
                                        <option value="">All</option>
                                        {this.props.clients?.map((client) => (
                                            <option key={client.id} value={client.id}>
                                                {client.name}
                                            </option>
                                        ))}

                                    </select>
                                </div>
                            </div>
                            <div className="d-flex justify-content-end mb-2 gap-2" style={{marginTop: "6%"}}>
                                <button
                                    className="btn btn-outline-danger px-4 py-2 rounded-lg text-sm font-bold"
                                    onClick={this.deleteAllCurrentPage}
                                >
                                    Delete All (Current Page)
                                </button>
                            </div>

                        </div>

                    </div>



                    <Datatable
                        className="table table-striped tableBackGround"
                        data={this.props.data}
                        header={this.props.headers}
                        totalRecords={this.props.totalRecords}
                        pageLimit={this.props.pageLimit}
                        sortColumn={this.props.sortColumn}
                        filter={this.props.onFilter}
                        onChangeOrder={this.props.onChangeOrder}
                        onLimitChanged={this.props.onLimitChanged}
                        onPageChanged={this.props.onPageChanged}
                        rowAction={this.actionsOptions}
                    />

                    {this.renderDataModal()}
                </div>
            </section>
        );
    }
}

const styles = {
    cell: {fontSize: "10pt", textAlign: "center", wordBreak: "break-word"},
};

export default connect(mapStateToProps, {
    ...DashboardActions,
})(Dashboard);